import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { 
  Brain, 
  CheckCircle2, 
  Clock, 
  ArrowRight, 
  RotateCcw,
  Target,
  Award
} from 'lucide-react';
import { SkillAssessment as SkillAssessmentType, AssessmentQuestion } from '@/types/learningPathTypes';

interface SkillAssessmentProps {
  assessment: SkillAssessmentType;
  onComplete: (score: number, recommendations: string[]) => void;
}

interface UserAnswer {
  questionId: string;
  selectedOption: number;
}

export const SkillAssessment: React.FC<SkillAssessmentProps> = ({
  assessment,
  onComplete
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState<UserAnswer[]>([]);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [score, setScore] = useState(0);

  const currentQuestion = assessment.questions[currentQuestionIndex];
  const totalQuestions = assessment.questions.length;
  const progress = ((currentQuestionIndex + 1) / totalQuestions) * 100;

  const handleAnswerSelect = (optionIndex: number) => {
    setSelectedOption(optionIndex);
  };

  const handleNextQuestion = () => {
    if (selectedOption === null) return;

    const newAnswer: UserAnswer = {
      questionId: currentQuestion.id,
      selectedOption
    };

    const updatedAnswers = [...userAnswers, newAnswer];
    setUserAnswers(updatedAnswers);

    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption(null);
    } else {
      // Assessment completed
      completeAssessment(updatedAnswers);
    }
  };

  const completeAssessment = (answers: UserAnswer[]) => {
    let totalScore = 0;
    let maxScore = 0;

    answers.forEach((answer) => {
      const question = assessment.questions.find(q => q.id === answer.questionId);
      if (question && question.correctAnswer !== undefined) {
        maxScore += question.points;
        if (answer.selectedOption === question.correctAnswer) {
          totalScore += question.points;
        }
      }
    });

    const percentageScore = Math.round((totalScore / maxScore) * 100);
    setScore(percentageScore);
    setIsCompleted(true);

    // Generate recommendations based on score
    const recommendations = generateRecommendations(percentageScore, assessment.skillAreas);
    onComplete(percentageScore, recommendations);
  };

  const generateRecommendations = (score: number, skillAreas: string[]): string[] => {
    if (score >= 80) {
      return [
        'You have strong foundational knowledge!',
        'Consider starting with intermediate-level courses',
        'Focus on advanced topics and specialization'
      ];
    } else if (score >= 60) {
      return [
        'You have good basic understanding',
        'Review fundamental concepts before advancing',
        'Start with beginner to intermediate courses'
      ];
    } else {
      return [
        'Start with foundation courses to build core skills',
        'Take time to practice basic concepts',
        'Consider prerequisite courses in ' + skillAreas.join(', ')
      ];
    }
  };

  const resetAssessment = () => {
    setCurrentQuestionIndex(0);
    setUserAnswers([]);
    setSelectedOption(null);
    setIsCompleted(false);
    setScore(0);
  };

  if (isCompleted) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <Award className="w-8 h-8 text-green-600" />
          </div>
          <CardTitle className="text-2xl">Assessment Complete!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 text-center">
          <div className="space-y-2">
            <div className="text-4xl font-bold text-primary">{score}%</div>
            <p className="text-muted-foreground">Your Overall Score</p>
          </div>

          <div className="space-y-3">
            <h4 className="font-semibold">Recommendations:</h4>
            <div className="space-y-2 text-left">
              {generateRecommendations(score, assessment.skillAreas).map((rec, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-sm">{rec}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 justify-center">
            <Button onClick={resetAssessment} variant="outline" className="gap-2">
              <RotateCcw className="w-4 h-4" />
              Retake Assessment
            </Button>
            <Button onClick={() => onComplete(score, generateRecommendations(score, assessment.skillAreas))}>
              View Recommended Path
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-6 h-6 text-primary" />
            <CardTitle>{assessment.name}</CardTitle>
          </div>
          <Badge variant="outline">
            {currentQuestionIndex + 1} of {totalQuestions}
          </Badge>
        </div>
        
        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {assessment.estimatedTime}
            </span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold leading-relaxed">
            {currentQuestion.question}
          </h3>

          {currentQuestion.type === 'multiple-choice' && currentQuestion.options && (
            <RadioGroup
              value={selectedOption?.toString() || ''}
              onValueChange={(value) => handleAnswerSelect(parseInt(value))}
              className="space-y-3"
            >
              {currentQuestion.options.map((option, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <RadioGroupItem value={index.toString()} id={`option-${index}`} />
                  <Label 
                    htmlFor={`option-${index}`} 
                    className="flex-1 cursor-pointer py-3 px-4 rounded-lg border border-muted hover:bg-muted/50 transition-colors"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          )}

          {currentQuestion.type === 'code' && (
            <div className="space-y-3">
              <div className="bg-muted p-4 rounded-lg">
                <pre className="text-sm">
                  <code>{currentQuestion.codeTemplate}</code>
                </pre>
              </div>
              {currentQuestion.options && (
                <RadioGroup
                  value={selectedOption?.toString() || ''}
                  onValueChange={(value) => handleAnswerSelect(parseInt(value))}
                  className="space-y-2"
                >
                  {currentQuestion.options.map((option, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <RadioGroupItem value={index.toString()} id={`code-option-${index}`} />
                      <Label htmlFor={`code-option-${index}`} className="cursor-pointer">
                        {option}
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-between items-center pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Target className="w-4 h-4" />
            <span>{currentQuestion.points} points</span>
          </div>
          
          <Button 
            onClick={handleNextQuestion}
            disabled={selectedOption === null}
            className="gap-2"
          >
            {currentQuestionIndex === totalQuestions - 1 ? 'Complete' : 'Next'}
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};